import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiTextField,
  Paper,
  Grid,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  hitlService,
  TaskStatus,
  type ExternalTaskDetailDto,
  type UpdateTaskStatusRequest,
} from '../services/hitlService';
import { useNotificationStore } from '../store/notificationStore';
import { PageDetailLayout } from '../components/layouts/PageDetailLayout';

const statusColor = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.Pending:
      return 'warning' as const;
    case TaskStatus.InProgress:
      return 'info' as const;
    case TaskStatus.Approved:
    case TaskStatus.Completed:
      return 'success' as const;
    case TaskStatus.Rejected:
      return 'error' as const;
    case TaskStatus.Cancelled:
      return 'default' as const;
    default:
      return 'default' as const;
  }
};

/**
 * Task detail page component.
 */
export const TaskDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { addNotification } = useNotificationStore();

  const [task, setTask] = useState<ExternalTaskDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [comments, setComments] = useState('');
  const [reason, setReason] = useState('');
  const [updating, setUpdating] = useState(false);

  const loadTask = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const data = await hitlService.getTask(id);
      setTask(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load task');
      addNotification('Failed to load task', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTask();
    // Refresh every 30 seconds for real-time updates
    const interval = setInterval(loadTask, 30000);
    return () => clearInterval(interval);
  }, [id]);

  const handleApproveClick = () => {
    setComments('');
    setApproveDialogOpen(true);
  };

  const handleRejectClick = () => {
    setReason('');
    setRejectDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!task || !id) return;

    setUpdating(true);
    try {
      const request: UpdateTaskStatusRequest = {
        status: TaskStatus.Approved,
        responseJson: comments ? JSON.stringify({ comments }) : undefined,
      };
      await hitlService.updateTaskStatus(id, request);
      addNotification('Task approved successfully', 'success');
      setApproveDialogOpen(false);
      setComments('');
      loadTask();
    } catch (err: any) {
      addNotification(err.message || 'Failed to approve task', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleReject = async () => {
    if (!task || !id || !reason.trim()) {
      addNotification('Reason is required for rejection', 'error');
      return;
    }

    setUpdating(true);
    try {
      const request: UpdateTaskStatusRequest = {
        status: TaskStatus.Rejected,
        responseJson: JSON.stringify({ reason: reason.trim() }),
      };
      await hitlService.updateTaskStatus(id, request);
      addNotification('Task rejected successfully', 'success');
      setRejectDialogOpen(false);
      setReason('');
      loadTask();
    } catch (err: any) {
      addNotification(err.message || 'Failed to reject task', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!task || !id || !comments.trim()) {
      addNotification('Comments are required for requesting changes', 'error');
      return;
    }

    setUpdating(true);
    try {
      const request: UpdateTaskStatusRequest = {
        status: TaskStatus.Pending,
        responseJson: JSON.stringify({ requestedChanges: comments.trim() }),
      };
      await hitlService.updateTaskStatus(id, request);
      addNotification('Changes requested successfully', 'success');
      setApproveDialogOpen(false);
      setComments('');
      loadTask();
    } catch (err: any) {
      addNotification(err.message || 'Failed to request changes', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const canApprove = task?.status === TaskStatus.Pending || task?.status === TaskStatus.InProgress;
  const canReject = task?.status === TaskStatus.Pending || task?.status === TaskStatus.InProgress;
  const canRequestChanges = task?.status === TaskStatus.Pending || task?.status === TaskStatus.InProgress;

  let contextData: any = null;
  if (task?.contextJson) {
    try {
      contextData = JSON.parse(task.contextJson);
    } catch {
      // Invalid JSON, ignore
    }
  }

  let responseData: any = null;
  if (task?.responseJson) {
    try {
      responseData = JSON.parse(task.responseJson);
    } catch {
      // Invalid JSON, ignore
    }
  }

  return (
    <>
      <PageDetailLayout
        title={task?.title || 'Task Details'}
        breadcrumbs={[{ label: 'Tasks', href: '/tasks' }, { label: task?.title || 'Task Details' }]}
        backTo={{ label: 'Tasks', href: '/tasks' }}
        loading={loading}
        error={error || undefined}
        onErrorDismiss={() => setError(null)}
        status={task ? { label: task.status, color: statusColor(task.status) } : undefined}
        chips={task ? [
          { label: task.taskType, color: 'primary' as const, variant: 'outlined' as const },
        ] : []}
        actions={[
          { icon: <RefreshIcon />, label: 'Refresh', onClick: loadTask, variant: 'outlined' as const, disabled: loading },
          ...(canApprove ? [{ icon: <ApproveIcon />, label: 'Approve', onClick: handleApproveClick, variant: 'contained' as const, color: 'success' as const }] : []),
          ...(canReject ? [{ icon: <RejectIcon />, label: 'Reject', onClick: handleRejectClick, variant: 'contained' as const, color: 'error' as const }] : []),
        ]}
      >
        {task && (
          <Grid container spacing={4}>
            {/* Main Content */}
            <Grid size={{ xs: 12, md: 8 }}>
              {/* Task Information */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  {task.description && (
                    <>
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                          Description
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {task.description}
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 3 }} />
                    </>
                  )}

                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                        External Task System
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {task.externalTaskSystemName}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                        External Task ID
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {task.externalTaskId}
                      </Typography>
                    </Grid>
                    {task.assignedTo && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                          Assigned To
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          User {task.assignedTo.substring(0, 8)}...
                        </Typography>
                      </Grid>
                    )}
                    {task.dueDate && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                          Due Date
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(task.dueDate).toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                        Created At
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(task.createdAt).toLocaleString()}
                      </Typography>
                    </Grid>
                    {task.completedAt && (
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                          Completed At
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(task.completedAt).toLocaleString()}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {/* Agent Context */}
              {contextData && (
                <Card sx={{ mb: 4 }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                      Agent Context
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {JSON.stringify(contextData, null, 2)}
                      </pre>
                    </Paper>
                  </CardContent>
                </Card>
              )}

              {/* Response */}
              {responseData && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                      Response
                    </Typography>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {JSON.stringify(responseData, null, 2)}
                      </pre>
                    </Paper>
                  </CardContent>
                </Card>
              )}
            </Grid>

            {/* Sidebar */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                    Quick Actions
                  </Typography>

                  {task.agentSessionId && (
                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{ mb: 2 }}
                      onClick={() => navigate(`/sessions/${task.agentSessionId}`)}
                    >
                      View Agent Session
                    </Button>
                  )}

                  {task.safetyGateExecutionId && (
                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{ mb: 2 }}
                      onClick={() => navigate(`/safety-gates/executions/${task.safetyGateExecutionId}`)}
                    >
                      View Safety Gate Execution
                    </Button>
                  )}

                  {canRequestChanges && (
                    <Button fullWidth variant="outlined" color="warning" onClick={handleApproveClick} sx={{ mb: 2 }}>
                      Request Changes
                    </Button>
                  )}

                  <Divider sx={{ my: 3 }} />

                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    Task History
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Created: {new Date(task.createdAt).toLocaleString()}
                  </Typography>
                  {task.completedAt && (
                    <Typography variant="body2" color="text.secondary">
                      Completed: {new Date(task.completedAt).toLocaleString()}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </PageDetailLayout>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Task</DialogTitle>
        <DialogContent>
          <MuiTextField
            fullWidth
            multiline
            rows={4}
            label="Comments (optional)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRequestChanges}
            variant="outlined"
            color="warning"
            disabled={updating || !comments.trim()}
          >
            {updating ? <CircularProgress size={20} /> : 'Request Changes'}
          </Button>
          <Button onClick={handleApprove} variant="contained" color="success" disabled={updating}>
            {updating ? <CircularProgress size={20} /> : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Task</DialogTitle>
        <DialogContent>
          <MuiTextField
            fullWidth
            multiline
            rows={4}
            label="Reason (required)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReject} variant="contained" color="error" disabled={updating || !reason.trim()}>
            {updating ? <CircularProgress size={20} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
